import React, { useCallback, useEffect, useState } from "react";

import {
  acknowledgeAIUsageAlert,
  AIUsageAlert,
  AIUsageMonthlySummary,
  getAIUsageAlerts,
  getAIUsageSummary,
} from "../api/client";


const USD_PER_GBP = 1.3;


function formatUsd(value: string): string {
  return `$${Number(value).toFixed(4)}`;
}


function formatGbpFromUsd(value: string): string {
  return `£${(Number(value) / USD_PER_GBP).toFixed(4)}`;
}


function featureLabel(feature: string): string {
  return feature
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}


export default function AIUsageDashboard() {
  const [summary, setSummary] = useState<AIUsageMonthlySummary | null>(null);
  const [alerts, setAlerts] = useState<AIUsageAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [acknowledgingId, setAcknowledgingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [nextSummary, nextAlerts] = await Promise.all([
        getAIUsageSummary(),
        getAIUsageAlerts("open"),
      ]);
      setSummary(nextSummary);
      setAlerts(nextAlerts);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not load AI usage monitoring."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsage();
  }, [loadUsage]);

  async function acknowledge(alertId: number) {
    setAcknowledgingId(alertId);
    setError(null);

    try {
      await acknowledgeAIUsageAlert(alertId);
      setAlerts((current) =>
        current.filter((alert) => alert.id !== alertId)
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not acknowledge this alert."
      );
    } finally {
      setAcknowledgingId(null);
    }
  }

  return (
    <section style={sectionStyle}>
      <div style={sectionHeadingStyle}>
        <div>
          <h2 style={{ margin: 0 }}>AI usage monitoring</h2>
          <p style={{ margin: "0.35rem 0 0", opacity: 0.75 }}>
            Review Sanctuary requests, estimated OpenAI cost, and fair-use
            warnings. Conversation content is not stored here.
          </p>
        </div>

        <button
          type="button"
          onClick={() => void loadUsage()}
          disabled={loading}
          style={secondaryButtonStyle}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      ) : null}

      <div style={summaryGridStyle}>
        <article style={summaryCardStyle}>
          <span style={cardLabelStyle}>Requests this month</span>
          <strong style={cardValueStyle}>
            {summary?.total_requests ?? "—"}
          </strong>
        </article>

        <article style={summaryCardStyle}>
          <span style={cardLabelStyle}>Estimated cost this month</span>
          <strong style={cardValueStyle}>
            {summary
              ? formatGbpFromUsd(summary.estimated_cost_usd)
              : "—"}
          </strong>
          {summary ? (
            <span style={{ opacity: 0.65, fontSize: "0.85rem" }}>
              {formatUsd(summary.estimated_cost_usd)} at the working conversion
            </span>
          ) : null}
        </article>

        <article style={summaryCardStyle}>
          <span style={cardLabelStyle}>Open alerts</span>
          <strong style={cardValueStyle}>{alerts.length}</strong>
        </article>
      </div>

      <div style={thresholdStyle}>
        <strong>Current safeguards</strong>
        <span>
          Member review points: 200 requests/day, 2,000/month, £1/day or
          £5/month.
        </span>
        <span>
          Business spending: £10 warning, £20 urgent, £30 operational service
          check.
        </span>
      </div>

      <div style={{ marginTop: "1.25rem" }}>
        <h3 style={{ marginBottom: "0.65rem" }}>Usage by feature</h3>

        {loading && !summary ? (
          <p style={{ opacity: 0.7 }}>Loading usage…</p>
        ) : summary && summary.by_feature.length > 0 ? (
          <div style={{ display: "grid", gap: "0.55rem" }}>
            {summary.by_feature.map((item) => (
              <div key={item.feature} style={featureRowStyle}>
                <strong>{featureLabel(item.feature)}</strong>
                <span>{item.request_count} requests</span>
                <span>
                  {formatGbpFromUsd(item.estimated_cost_usd)}
                  {" · "}
                  {formatUsd(item.estimated_cost_usd)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ opacity: 0.7 }}>No AI requests recorded this month.</p>
        )}
      </div>

      <div style={{ marginTop: "1.25rem" }}>
        <h3 style={{ marginBottom: "0.65rem" }}>Open alerts</h3>

        {!loading && alerts.length === 0 ? (
          <p style={{ opacity: 0.75 }}>
            No open usage alerts. Sanctuary is operating within the current
            review points.
          </p>
        ) : (
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {alerts.map((alert) => (
              <article key={alert.id} style={alertStyle}>
                <div style={alertHeadingStyle}>
                  <div>
                    <span style={levelStyle(alert.level)}>
                      {alert.level.toUpperCase()}
                    </span>
                    <strong style={{ marginLeft: "0.6rem" }}>
                      {alert.scope === "member"
                        ? alert.member_display_name ||
                          alert.member_email ||
                          "Member usage"
                        : "Business-wide usage"}
                    </strong>
                  </div>
                  <span style={{ opacity: 0.65, fontSize: "0.85rem" }}>
                    {new Date(alert.created_at).toLocaleString()}
                  </span>
                </div>

                <p style={{ margin: "0.75rem 0" }}>{alert.message}</p>

                <div style={alertDetailsStyle}>
                  <span>{alert.request_count} requests</span>
                  <span>
                    Estimated cost:{" "}
                    {formatGbpFromUsd(alert.estimated_cost_usd)}
                  </span>
                  {alert.member_email ? (
                    <span>{alert.member_email}</span>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={() => void acknowledge(alert.id)}
                  disabled={acknowledgingId === alert.id}
                  style={secondaryButtonStyle}
                >
                  {acknowledgingId === alert.id
                    ? "Acknowledging…"
                    : "Acknowledge alert"}
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}


const sectionStyle: React.CSSProperties = {
  padding: "1rem",
  borderRadius: 14,
  border: "1px solid #1d4f68",
  background: "#07131b",
  marginBottom: "1rem",
};

const sectionHeadingStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "1rem",
  flexWrap: "wrap",
};

const summaryGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "0.75rem",
  marginTop: "1rem",
};

const summaryCardStyle: React.CSSProperties = {
  padding: "0.9rem",
  borderRadius: 12,
  border: "1px solid #243b4a",
  background: "#0a1a24",
  display: "grid",
  gap: "0.3rem",
};

const cardLabelStyle: React.CSSProperties = {
  opacity: 0.72,
  fontSize: "0.9rem",
};

const cardValueStyle: React.CSSProperties = {
  fontSize: "1.55rem",
  color: "#38bdf8",
};

const thresholdStyle: React.CSSProperties = {
  marginTop: "0.9rem",
  padding: "0.8rem",
  borderRadius: 10,
  border: "1px solid #334155",
  background: "#0b1018",
  display: "grid",
  gap: "0.35rem",
  fontSize: "0.9rem",
};

const featureRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(160px, 1fr) auto auto",
  gap: "1rem",
  padding: "0.7rem 0.8rem",
  borderRadius: 10,
  border: "1px solid #24324a",
  background: "#080b10",
  alignItems: "center",
};

const alertStyle: React.CSSProperties = {
  padding: "0.9rem",
  borderRadius: 12,
  border: "1px solid #7c5b19",
  background: "#1b1507",
};

const alertHeadingStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "0.75rem",
  flexWrap: "wrap",
};

const alertDetailsStyle: React.CSSProperties = {
  display: "flex",
  gap: "1rem",
  flexWrap: "wrap",
  marginBottom: "0.75rem",
  opacity: 0.75,
  fontSize: "0.9rem",
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: "0.5rem 0.8rem",
  borderRadius: 999,
  border: "1px solid #64748b",
  background: "transparent",
  color: "#e5e5e5",
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  marginTop: "1rem",
  padding: "0.75rem",
  borderRadius: 10,
  border: "1px solid #7f1d1d",
  background: "#2a0d0d",
  color: "#fecaca",
};

function levelStyle(level: string): React.CSSProperties {
  return {
    display: "inline-block",
    padding: "0.2rem 0.5rem",
    borderRadius: 999,
    fontSize: "0.75rem",
    fontWeight: 800,
    color: level === "urgent" ? "#fecaca" : "#fde68a",
    border:
      level === "urgent"
        ? "1px solid #991b1b"
        : "1px solid #a16207",
  };
}
